import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { BackHandler, pushSceneState } from '../systems/BackButton';

const SWIPE_THRESHOLD = 50;
const PAGE_COUNT = 7;
const DOT_SPACING = 24;

export class HowToPlayScene extends Phaser.Scene implements BackHandler {
  private currentPage = 0;
  private returnTo = 'TitleScene';
  private pageContainer!: Phaser.GameObjects.Container;
  private dots: Phaser.GameObjects.Arc[] = [];
  private pointerStartX = 0;
  private pointerDown = false;
  private leftArrow!: Phaser.GameObjects.Text;
  private rightArrow!: Phaser.GameObjects.Text;

  constructor() {
    super('HowToPlayScene');
  }

  handleBack(): void {
    this.close();
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data.returnTo ?? 'TitleScene';
    this.currentPage = 0;
    this.pointerDown = false;
  }

  preload(): void {
    const tiles = [
      'moss-wetland', 'moss-forest', 'fern-wetland', 'flower-alpine',
      'mushroom-wetland', 'shell-coral', 'vine-savanna',
      'sacred-spring', 'seed', 'wave',
      'ground-degraded', 'ground-wetland', 'ground-forest',
    ];
    for (const key of tiles) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/tiles/${key}.png`);
      }
    }
    if (!this.textures.exists('volcano')) {
      this.load.image('volcano', 'assets/icons/volcano.png');
    }
    const spirits = ['spirit-dryad', 'spirit-shade', 'spirit-luna', 'spirit-lumina'];
    for (const key of spirits) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/icons/${key}.png`);
      }
    }
  }

  create(): void {
    pushSceneState();
    getMusicPlayer().startMenu();

    const centerX = GAME_CONFIG.width / 2;

    const closeBtn = this.add.text(GAME_CONFIG.width - 28, 30, '✕', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.close();
    });

    this.add.text(centerX, 40, 'How to Play', {
      fontSize: '24px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.pageContainer = this.add.container(0, 0);
    this.showPage(0);

    this.createDots();
    this.createArrows();
    this.setupSwipe();
  }

  private createDots(): void {
    const centerX = GAME_CONFIG.width / 2;
    const dotsY = GAME_CONFIG.height - 60;
    const totalWidth = (PAGE_COUNT - 1) * DOT_SPACING;
    const startX = centerX - totalWidth / 2;

    for (let i = 0; i < PAGE_COUNT; i++) {
      const dot = this.add.circle(startX + i * DOT_SPACING, dotsY, 6, 0xffffff);
      dot.setAlpha(i === 0 ? 1 : 0.3);
      this.dots.push(dot);
    }
  }

  private createArrows(): void {
    const arrowY = GAME_CONFIG.height / 2;

    this.leftArrow = this.add.text(20, arrowY, '‹', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    this.leftArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.prevPage();
    });

    this.rightArrow = this.add.text(GAME_CONFIG.width - 20, arrowY, '›', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    this.rightArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.nextPage();
    });

    this.updateArrows();
  }

  private setupSwipe(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.pointerDown = true;
      this.pointerStartX = pointer.x;
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      this.pointerDown = false;
      const dx = pointer.x - this.pointerStartX;
      if (dx < -SWIPE_THRESHOLD) {
        this.nextPage();
      } else if (dx > SWIPE_THRESHOLD) {
        this.prevPage();
      }
    });
  }

  private nextPage(): void {
    if (this.currentPage < PAGE_COUNT - 1) {
      this.currentPage++;
      this.showPage(this.currentPage);
      this.updateDots();
      this.updateArrows();
    }
  }

  private prevPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.showPage(this.currentPage);
      this.updateDots();
      this.updateArrows();
    }
  }

  private updateDots(): void {
    for (let i = 0; i < this.dots.length; i++) {
      this.dots[i].setAlpha(i === this.currentPage ? 1 : 0.3);
    }
  }

  private updateArrows(): void {
    this.leftArrow.setAlpha(this.currentPage === 0 ? 0.2 : 1);
    this.rightArrow.setAlpha(this.currentPage === PAGE_COUNT - 1 ? 0.2 : 1);
  }

  private showPage(index: number): void {
    this.pageContainer.removeAll(true);

    switch (index) {
      case 0: this.buildPagePlacing(); break;
      case 1: this.buildPageDiscard(); break;
      case 2: this.buildPageLineClears(); break;
      case 3: this.buildPageSpiritPowers(); break;
      case 4: this.buildPageStreaks(); break;
      case 5: this.buildPageWinCondition(); break;
      case 6: this.buildPageStarsAndTimeAttack(); break;
    }
  }

  private buildPagePlacing(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 160;
    const tileSize = 56;

    const ground1 = this.add.image(centerX - tileSize / 2, baseY + 100, 'ground-wetland');
    ground1.setDisplaySize(tileSize, tileSize);
    this.pageContainer.add(ground1);

    const existing = this.add.image(centerX - tileSize / 2, baseY + 100, 'moss-wetland');
    existing.setDisplaySize(tileSize - 8, tileSize - 8);
    this.pageContainer.add(existing);

    const ground2 = this.add.image(centerX + tileSize / 2, baseY + 100, 'ground-degraded');
    ground2.setDisplaySize(tileSize, tileSize);
    this.pageContainer.add(ground2);

    const newTile = this.add.image(centerX + tileSize / 2, baseY + 100, 'fern-wetland');
    newTile.setDisplaySize(tileSize - 8, tileSize - 8);
    this.pageContainer.add(newTile);

    const tileBase = newTile.scaleX;
    this.tweens.add({
      targets: newTile,
      alpha: { from: 0.4, to: 1 },
      scaleX: { from: tileBase * 0.85, to: tileBase * 1.15 },
      scaleY: { from: tileBase * 0.85, to: tileBase * 1.15 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });

    const arrow = this.add.text(centerX, baseY + 100, '→', {
      fontSize: '24px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(arrow);

    const matchLabel = this.add.text(centerX, baseY + 160, 'Same color = match!', {
      fontSize: '14px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(matchLabel);

    const ground3 = this.add.image(centerX - tileSize / 2, baseY + 240, 'ground-forest');
    ground3.setDisplaySize(tileSize, tileSize);
    this.pageContainer.add(ground3);

    const existing2 = this.add.image(centerX - tileSize / 2, baseY + 240, 'moss-forest');
    existing2.setDisplaySize(tileSize - 8, tileSize - 8);
    this.pageContainer.add(existing2);

    const ground4 = this.add.image(centerX + tileSize / 2, baseY + 240, 'ground-degraded');
    ground4.setDisplaySize(tileSize, tileSize);
    this.pageContainer.add(ground4);

    const newTile2 = this.add.image(centerX + tileSize / 2, baseY + 240, 'moss-wetland');
    newTile2.setDisplaySize(tileSize - 8, tileSize - 8);
    this.pageContainer.add(newTile2);

    const tileBase2 = newTile2.scaleX;
    this.tweens.add({
      targets: newTile2,
      alpha: { from: 0.4, to: 1 },
      scaleX: { from: tileBase2 * 0.85, to: tileBase2 * 1.15 },
      scaleY: { from: tileBase2 * 0.85, to: tileBase2 * 1.15 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      delay: 400,
    });

    const arrow2 = this.add.text(centerX, baseY + 240, '→', {
      fontSize: '24px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(arrow2);

    const matchLabel2 = this.add.text(centerX, baseY + 300, 'Same symbol = match!', {
      fontSize: '14px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(matchLabel2);

    const desc = this.add.text(centerX, baseY + 380, 'Place tiles next to matching\ncolors or symbols.', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(desc);
  }

  private buildPageDiscard(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 140;

    const volcano = this.add.image(centerX, baseY + 70, 'volcano');
    volcano.setDisplaySize(80, 80);
    this.pageContainer.add(volcano);

    const tile = this.add.image(centerX, baseY - 10, 'mushroom-wetland');
    tile.setDisplaySize(44, 44);
    this.pageContainer.add(tile);

    this.tweens.add({
      targets: tile,
      y: baseY + 50,
      alpha: 0,
      scale: 0.3,
      duration: 1200,
      repeat: -1,
      repeatDelay: 600,
    });

    const desc = this.add.text(centerX, baseY + 170, 'Tap the volcano to discard\nthe current tile.\n\nEach discard raises the lava.\nToo many discards and the volcano erupts!', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(desc);

    const hintY = baseY + 320;

    const hintTile = this.add.image(centerX, hintY, 'fern-wetland');
    hintTile.setDisplaySize(48, 48);
    this.pageContainer.add(hintTile);

    this.tweens.add({
      targets: hintTile,
      alpha: 0.3,
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
      onUpdate: () => {
        hintTile.setTint(0xff4444);
      },
    });

    const hintDesc = this.add.text(centerX, hintY + 50, 'If a tile blinks red, it can\'t\nbe placed — discard it!', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(hintDesc);
  }

  private buildPageLineClears(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 200;
    const tileSize = 48;
    const tiles = ['moss-wetland', 'fern-wetland', 'flower-alpine', 'mushroom-wetland', 'shell-coral'];
    const startX = centerX - (tiles.length - 1) * tileSize / 2;

    for (let i = 0; i < tiles.length; i++) {
      const x = startX + i * tileSize;

      const ground = this.add.image(x, baseY + 80, 'ground-degraded');
      ground.setDisplaySize(tileSize - 2, tileSize - 2);
      this.pageContainer.add(ground);

      const sprite = this.add.image(x, baseY + 80, tiles[i]);
      sprite.setDisplaySize(tileSize - 10, tileSize - 10);
      this.pageContainer.add(sprite);

      this.tweens.add({
        targets: [ground, sprite],
        alpha: { from: 1, to: 0.3 },
        duration: 600,
        delay: 1500 + i * 100,
        yoyo: true,
        repeat: -1,
        repeatDelay: 2000,
      });
    }

    const restoredRow: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < tiles.length; i++) {
      const x = startX + i * tileSize;
      const restored = this.add.image(x, baseY + 80, 'ground-wetland');
      restored.setDisplaySize(tileSize - 2, tileSize - 2);
      restored.setAlpha(0);
      this.pageContainer.add(restored);
      restoredRow.push(restored);
    }

    this.tweens.add({
      targets: restoredRow,
      alpha: { from: 0, to: 1 },
      duration: 400,
      delay: 2200,
      yoyo: true,
      repeat: -1,
      repeatDelay: 2400,
    });

    const desc = this.add.text(centerX, baseY + 200, 'Fill a row or column\nto clear and restore the land.', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(desc);
  }

  private buildPageSpiritPowers(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 100;
    const iconSize = 52;
    const rowSpacing = 150;

    const powers = [
      {
        icon: 'seed',
        name: 'Seed',
        earn: 'Mono-color line, or Rhythm streak',
        effect: 'Place anywhere — no match needed',
      },
      {
        icon: 'sacred-spring',
        name: 'Sacred Spring',
        earn: '3+ colors AND 4+ symbols in one line',
        effect: 'Wildcard — matches any tile',
      },
      {
        icon: 'wave',
        name: 'Wave',
        earn: 'Mono-symbol line, or Rhythm streak',
        effect: 'Removes any tile from the board',
      },
    ];

    const header = this.add.text(centerX, baseY, 'Clear lines to earn powers!', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(header);

    const contentLeft = 76;
    const textLeft = contentLeft + iconSize / 2 + 8;

    for (let i = 0; i < powers.length; i++) {
      const y = baseY + 60 + i * rowSpacing;
      const power = powers[i];

      const icon = this.add.image(contentLeft, y + 30, power.icon);
      icon.setDisplaySize(iconSize, iconSize);
      this.pageContainer.add(icon);

      const baseScale = icon.scaleX;
      this.tweens.add({
        targets: icon,
        scaleX: { from: baseScale, to: baseScale * 1.08 },
        scaleY: { from: baseScale, to: baseScale * 1.08 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        delay: i * 200,
      });

      const name = this.add.text(textLeft, y + 10, power.name, {
        fontSize: '17px',
        color: '#a8e6cf',
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(name);

      const earn = this.add.text(textLeft, y + 32, `Earn: ${power.earn}`, {
        fontSize: '13px',
        color: '#cccccc',
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(earn);

      const effect = this.add.text(textLeft, y + 52, `Effect: ${power.effect}`, {
        fontSize: '13px',
        color: '#888888',
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(effect);
    }
  }

  private buildPageStreaks(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 80;
    const iconSize = 48;
    const rowSpacing = 120;

    const header = this.add.text(centerX, baseY, 'Streaks & Shields', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(header);

    const streaks = [
      {
        icon: 'spirit-dryad',
        name: 'Harmony',
        desc: 'Place without discarding.\n×1.5 at 15, ×2 at 25, ×3 at 35.',
        color: '#6bcb77',
      },
      {
        icon: 'spirit-shade',
        name: 'Patience',
        desc: 'Place without clearing.\nShields at 20+, Seed at 28+.',
        color: '#9b59b6',
      },
      {
        icon: 'spirit-luna',
        name: 'Rhythm',
        desc: 'Clear lines in a row.\nSeed/Wave at 2, Spring at 3+.',
        color: '#7ec8e3',
      },
      {
        icon: 'spirit-lumina',
        name: 'Shields',
        desc: 'Block volcano heat.\nEarned from Patience streaks.',
        color: '#fff0c0',
      },
    ];

    const contentLeft = 110;
    const textLeft = contentLeft + iconSize / 2 + 12;

    for (let i = 0; i < streaks.length; i++) {
      const y = baseY + 50 + i * rowSpacing;
      const streak = streaks[i];

      const icon = this.add.image(contentLeft, y + 24, streak.icon);
      icon.setDisplaySize(iconSize, iconSize);
      this.pageContainer.add(icon);

      const baseScale = icon.scaleX;
      this.tweens.add({
        targets: icon,
        scaleX: { from: baseScale, to: baseScale * 1.08 },
        scaleY: { from: baseScale, to: baseScale * 1.08 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        delay: i * 200,
      });

      const name = this.add.text(textLeft, y + 8, streak.name, {
        fontSize: '17px',
        color: streak.color,
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(name);

      const desc = this.add.text(textLeft, y + 36, streak.desc, {
        fontSize: '13px',
        color: '#cccccc',
        fontFamily: 'sans-serif',
        lineSpacing: 4,
      }).setOrigin(0, 0.5);
      this.pageContainer.add(desc);
    }
  }

  private buildPageWinCondition(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 180;
    const tileSize = 40;
    const gridCount = 4;
    const startX = centerX - (gridCount - 1) * tileSize / 2;
    const startY = baseY + 60;

    for (let row = 0; row < gridCount; row++) {
      for (let col = 0; col < gridCount; col++) {
        const x = startX + col * tileSize;
        const y = startY + row * tileSize;

        const ground = this.add.image(x, y, 'ground-wetland');
        ground.setDisplaySize(tileSize - 2, tileSize - 2);
        this.pageContainer.add(ground);
      }
    }

    this.tweens.add({
      targets: this.pageContainer.getAll(),
      alpha: { from: 0.6, to: 1 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
    });

    const checkmark = this.add.text(centerX, startY + gridCount * tileSize + 40, '✓', {
      fontSize: '48px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(checkmark);

    const desc = this.add.text(centerX, startY + gridCount * tileSize + 110, 'Restore all tiles\nto complete the level.', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(desc);
  }

  private buildPageStarsAndTimeAttack(): void {
    const centerX = GAME_CONFIG.width / 2;
    const baseY = 80;

    const header = this.add.text(centerX, baseY, 'Stars & Time Attack', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(header);

    const starGold = this.add.text(centerX - 60, baseY + 50, '★ ★ ★', {
      fontSize: '24px',
      color: '#ffd700',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(starGold);

    const starLabel = this.add.text(centerX + 30, baseY + 50, 'Score Stars', {
      fontSize: '16px',
      color: '#ffd700',
      fontFamily: 'sans-serif',
    }).setOrigin(0, 0.5);
    this.pageContainer.add(starLabel);

    const starDesc = this.add.text(centerX, baseY + 90, 'Earn gold stars by scoring well.\nHigher scores = more stars.', {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(starDesc);

    const divider = this.add.rectangle(centerX, baseY + 140, 200, 1, 0x555555);
    this.pageContainer.add(divider);

    const starBlue = this.add.text(centerX - 60, baseY + 180, '★ ★ ★', {
      fontSize: '24px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(starBlue);

    const timeLabel = this.add.text(centerX + 30, baseY + 180, 'Time Stars', {
      fontSize: '16px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0, 0.5);
    this.pageContainer.add(timeLabel);

    const timeDesc = this.add.text(centerX, baseY + 220, 'Earn blue stars by finishing fast\nin Time Attack mode.', {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(timeDesc);

    const divider2 = this.add.rectangle(centerX, baseY + 265, 200, 1, 0x555555);
    this.pageContainer.add(divider2);

    const taHeader = this.add.text(centerX, baseY + 300, '⏱ Time Attack', {
      fontSize: '18px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(taHeader);

    const taDesc = this.add.text(centerX, baseY + 350, 'The volcano fills on its own!\nPlace tiles and clear lines\nto cool it down.', {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'sans-serif',
      align: 'center',
      lineSpacing: 4,
    }).setOrigin(0.5);
    this.pageContainer.add(taDesc);

    const unlockDesc = this.add.text(centerX, baseY + 430, 'Beat a level first to unlock\nits Time Attack on the World Map.', {
      fontSize: '14px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);
    this.pageContainer.add(unlockDesc);

    this.tweens.add({
      targets: [starGold, starBlue],
      scaleX: { from: 1, to: 1.1 },
      scaleY: { from: 1, to: 1.1 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });
  }

  private close(): void {
    if (this.returnTo === 'GameScene') {
      this.scene.stop();
      this.scene.wake('GameScene');
    } else {
      this.scene.start(this.returnTo);
    }
  }
}
